/**
 * Route and navigation permission metadata — shared between frontend guards.
 */

import type { PermissionAction, PermissionCategory } from './types/User';

export interface RoutePermission {
  path: string;
  /** Exact match (e.g. dashboard `/`) */
  exact?: boolean;
  category: PermissionCategory;
  action: PermissionAction;
  /** Human-readable label for access-denied messages */
  label: string;
}

/** Central route permission map — keep in sync with App.tsx routes and sidebar */
export const ROUTE_PERMISSIONS: RoutePermission[] = [
  { path: '/', exact: true, category: 'analytics', action: 'view', label: 'Dashboard' },
  { path: '/facility', category: 'analytics', action: 'view', label: 'Facility' },
  { path: '/rooms', category: 'rooms', action: 'view', label: 'Rooms' },
  { path: '/analytics', category: 'analytics', action: 'view', label: 'Analytics' },
  { path: '/climate', category: 'sensors', action: 'view', label: 'Climate' },
  { path: '/hvac', category: 'devices', action: 'view', label: 'HVAC' },
  { path: '/sensors', category: 'sensors', action: 'view', label: 'Sensors' },
  { path: '/alerts', category: 'alerts', action: 'view', label: 'Alerts' },
  { path: '/devices', category: 'devices', action: 'view', label: 'Devices' },
  { path: '/rules', category: 'rules', action: 'view', label: 'Rules' },
  { path: '/schedules', category: 'rules', action: 'view', label: 'Schedules' },
  { path: '/io-mapping', category: 'hardware', action: 'view', label: 'I/O Mapping' },
  { path: '/irrigation', category: 'irrigation', action: 'view', label: 'Irrigation' },
  { path: '/batch-integration', category: 'cultivation', action: 'view', label: 'Batches' },
  { path: '/recipe-presets', category: 'cultivation', action: 'view', label: 'Recipe Presets' },
  { path: '/logs', category: 'logs', action: 'view', label: 'Logs' },
  { path: '/users', category: 'users', action: 'view', label: 'User Management' },
  // Grok and other AI agents (ADR-0020): owner only, like the server's manager (functions/agents/api.js personOf)
  { path: '/ai-agents', category: 'ai', action: 'admin', label: 'AI agents' },
  { path: '/settings', category: 'settings', action: 'view', label: 'Settings' },
];

export interface NavItemPermission {
  to: string;
  label: string;
  category: PermissionCategory;
  action: PermissionAction;
  exact?: boolean;
  badge?: number;
}

export interface NavSectionPermission {
  id: string;
  title: string;
  items: NavItemPermission[];
}

export const NAV_SECTIONS: NavSectionPermission[] = [
  {
    id: 'overview',
    title: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', category: 'analytics', action: 'view', exact: true },
      { to: '/facility', label: 'Facility', category: 'analytics', action: 'view' },
      { to: '/rooms', label: 'Rooms', category: 'rooms', action: 'view' },
      { to: '/analytics', label: 'Analytics', category: 'analytics', action: 'view' },
    ],
  },
  {
    id: 'climate',
    title: 'Climate',
    items: [
      { to: '/climate', label: 'Climate', category: 'sensors', action: 'view' },
      { to: '/hvac', label: 'HVAC', category: 'devices', action: 'view' },
      { to: '/sensors', label: 'Sensors', category: 'sensors', action: 'view' },
    ],
  },
  {
    id: 'control',
    title: 'Control',
    items: [
      { to: '/devices', label: 'Devices', category: 'devices', action: 'view' },
      { to: '/rules', label: 'Rules', category: 'rules', action: 'view' },
      { to: '/schedules', label: 'Schedules', category: 'rules', action: 'view' },
      { to: '/io-mapping', label: 'I/O Mapping', category: 'hardware', action: 'view' },
    ],
  },
  {
    id: 'cultivation',
    title: 'Cultivation',
    items: [
      { to: '/irrigation', label: 'Irrigation', category: 'irrigation', action: 'view' },
      { to: '/batch-integration', label: 'Batches', category: 'cultivation', action: 'view' },
      { to: '/recipe-presets', label: 'Recipe Presets', category: 'cultivation', action: 'view' },
    ],
  },
  {
    id: 'system',
    title: 'System',
    items: [
      { to: '/alerts', label: 'Alerts', category: 'alerts', action: 'view' },
      { to: '/logs', label: 'Logs', category: 'logs', action: 'view' },
      { to: '/users', label: 'Users', category: 'users', action: 'view' },
      { to: '/ai-agents', label: 'AI agents', category: 'ai', action: 'admin' },
      { to: '/settings', label: 'Settings', category: 'settings', action: 'view' },
    ],
  },
];

/** Resolve permission requirement for a pathname */
export function getRoutePermission(pathname: string): RoutePermission | null {
  // Room-specific routes inherit parent permission
  const normalized = pathname.split('?')[0];

  // Special aliases
  if (normalized.startsWith('/rooms/') && normalized.includes('/irrigation')) {
    return ROUTE_PERMISSIONS.find(r => r.path === '/irrigation') ?? null;
  }
  if (normalized.startsWith('/rooms/') && normalized.includes('/climate')) {
    return ROUTE_PERMISSIONS.find(r => r.path === '/climate') ?? null;
  }
  if (normalized.startsWith('/rooms/') && !normalized.includes('/')) {
    return ROUTE_PERMISSIONS.find(r => r.path === '/rooms') ?? null;
  }
  if (normalized.startsWith('/rooms/')) {
    return ROUTE_PERMISSIONS.find(r => r.path === '/rooms') ?? null;
  }
  if (normalized.startsWith('/irrigation/')) {
    return ROUTE_PERMISSIONS.find(r => r.path === '/irrigation') ?? null;
  }
  if (normalized.startsWith('/climate/')) {
    return ROUTE_PERMISSIONS.find(r => r.path === '/climate') ?? null;
  }

  // Exact match first
  const exact = ROUTE_PERMISSIONS.find(r => r.exact && r.path === normalized);
  if (exact) return exact;

  // Prefix match (longest path wins)
  const matches = ROUTE_PERMISSIONS
    .filter(r => !r.exact && (r.path === '/' ? normalized === '/' : normalized.startsWith(r.path)))
    .sort((a, b) => b.path.length - a.path.length);
  return matches[0] ?? null;
}

/** First route the user can access — used as post-login redirect for scoped users */
export function getDefaultRoute(
  canFn: (category: PermissionCategory, action: PermissionAction) => boolean
): string {
  const priority = ['/irrigation', '/climate', '/devices', '/analytics', '/', '/settings'];
  for (const path of priority) {
    const perm = ROUTE_PERMISSIONS.find(r => r.path === path);
    if (perm && canFn(perm.category, perm.action)) return path;
  }
  for (const perm of ROUTE_PERMISSIONS) {
    if (canFn(perm.category, perm.action)) return perm.path;
  }
  return '/access-denied';
}
